import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import { PatchGeodataRequestBody } from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { alignerGeodataIncludes } from '../use-geodata';

type UseSaveGeodataMutationProps = {
  geodataId: string;
  onSuccess?: (data: GeodataModel) => void;
};

export const useSaveGeodataMutation = (props: UseSaveGeodataMutationProps) => {
  const { clearErrors: clearApiErrors, handleError, buildErrorList } = useErrorHandling();
  const queryClient = useQueryClient();

  const saveQuery = useMutation({
    mutationFn: (body: PatchGeodataRequestBody) => {
      return SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().patchGeodata({
        body,
        path: {
          geodataId: props.geodataId,
        },
        query: {
          include: alignerGeodataIncludes,
        },
      });
    },
    retry: false,
    networkMode: 'always',
    onSuccess: (response) => {
      // Update the geodata in React Query cache immediately
      queryClient.setQueryData<GeodataModel>(['fetchGeodataById', props.geodataId], response.data);
      props.onSuccess?.(response.data);
    },
    onMutate: () => clearApiErrors(),
    onError: (err) => handleError(err),
  });

  const errorList = useMemo(() => buildErrorList(), [buildErrorList]);

  return {
    saveQuery,
    errorList,
  };
};
